import { useEffect, useState } from "react";

import { format } from "date-fns";
import { useProductStore } from "@/store/product";
import { useActiveStore } from "@/store/activePages";
import { useFormStore } from "@/store/form";
import { Loyalty, Plan, ProductsFetch } from "@/api/products";
import Column from "@/components/Column";
import Columns from "@/components/Columns";
import PageHeader from "@/components/PageHeader";
import { useUserJourney } from "@/api/userJourney";
import { useOptions } from "@/api/options";
import { removeAccentAndSpace } from "@/utils/string";
import useBreakpoint from "@/hooks/useBreakpoint";
import { useNavigate, useParams } from "react-router-dom";
import { payments } from "@/app/personal-data/contants";

export const ResumePage = () => {
  const params = useParams() as { uuid: string };
  const navigate = useNavigate();
  const { journey: userJourney, error } = useUserJourney(params.uuid);
  const { data: products } = ProductsFetch();

  const addData = useFormStore((store) => store.addData);
  const addPage = useActiveStore((store) => store.addPage);
  const setProduct = useProductStore((store) => store.setProduct);
  const { result: data } = useOptions();

  const [isEmpty, setIsEmpty] = useState(false);
  const [updated, setUpdated] = useState(false);

  const sortFidelitiesByDDD = (fidelities: Loyalty[], ddd: string) => {
    const fidelitiesFormated = fidelities.map((fidelity: Loyalty) => ({
      ...fidelity,
      precos: [
        ...fidelity.precos.filter((price) => price.ddd === ddd),
        ...fidelity.precos.filter((price) => price.ddd !== ddd),
      ],
    }));
    return [
      ...fidelitiesFormated.filter(
        (fidelity) => !!fidelity.precos.find((price) => price.ddd === ddd)
      ),
      ...fidelitiesFormated.filter(
        (fidelity) => !!fidelity.precos.find((price) => price.ddd !== ddd)
      ),
    ];
  };

  const isMultProduct = import.meta.env.VITE_CHOOSE_PRODUCT_TYPE === "1";
  const { isOnBreakpoint: isTableOrLess } = useBreakpoint(1024);

  useEffect(() => {
    if (error || (userJourney && userJourney.venda_id)) {
      navigate(`/`);
    }

    if (!updated && userJourney && products && !userJourney.venda_id && data) {
      const pages = ["/personal-data"];

      const zipCodeWithDash = userJourney.cep
        ? `${userJourney.cep.slice(0, 5)}-${userJourney.cep.slice(5)}`
        : "";

      addData({
        address: {
          zipCode: zipCodeWithDash,
          street: userJourney.logradouro,
          neighborhood: userJourney.bairro,
          number: userJourney.numero || "",
          complement: userJourney.complemento || "",
          referencePoint: userJourney.ponto_referencia || "",
          noNumber: userJourney.sem_numero || false,
          stateCode: userJourney.uf,
          cityName: userJourney.cidade,
        },
      });

      addData({
        payment: {
          debtDueDate: userJourney.vencimento,
          knownClausules: true,
          knownTerms: true,
        },
      });

      addData({
        cpf: userJourney.cpf,
        birthDate: format(new Date(userJourney.nascimento), "dd/MM/yyyy"),
        email: userJourney.email,
        fullName: userJourney.nome_completo,
        motherName: userJourney.filiacao,
        tel: userJourney.msisdn ? userJourney.msisdn : "",
        tipo: userJourney.tipo,
        utm_campaign: userJourney.utm_campaign,
        utm_medium: userJourney.utm_medium,
        utm_source: userJourney.utm_source,
        uuid: userJourney.uuid,
        retomado: true,
        // A API da retomada não informa o slug; não reutilizar o de outra oferta.
        ofertaSlug: "",
      });

      if (userJourney.forma_pagamento) {
        const paymentsFilterd =
          payments[removeAccentAndSpace(userJourney.forma_pagamento)];

        addData({
          paymentType: {
            ...paymentsFilterd,
          },
        });
      }

      const currentProducts = [];
      const ddd = userJourney.msisdn ? userJourney.msisdn.slice(0, 2) : "11";

      for (let i = 0; i < products.length; i++) {
        const product = products[i] as Plan;

        const hasStateOffer = product.precos.find((price) => price.ddd === ddd);

        if (hasStateOffer) {
          currentProducts.push({
            ...product,
            precos: product.precos,
            fidelidades: sortFidelitiesByDDD(product.fidelidades, ddd),
          });
        }
      }

      const offerProduct = currentProducts.find(
        (product) => product.tim_id === userJourney.plan.tim_id
      );

      if (!offerProduct) {
        navigate(`/`);
        return;
      }

      setProduct(offerProduct!);
      setUpdated(true);

      pages.forEach((page) => {
        addPage(page);
        navigate(page);
      });
    }
  }, [
    products,
    updated,
    setProduct,
    addPage,
    userJourney,
    addData,
    error,
    data,
    isMultProduct,
    isTableOrLess,
    navigate,
  ]);

  useEffect(() => {
    if (userJourney && products?.length === 0) {
      setIsEmpty(true);
    }
  }, [userJourney, products]);

  if (isEmpty) {
    return (
      <div className="min-w-screen flex min-h-screen flex-col px-2 pt-10">
        <Column size={12} className="text-center">
          <PageHeader
            title="Desculpe, mas não temos planos TIM disponíveis em seu estado."
            dataIcTitle="sec-omega-comunicacao"
          />
        </Column>
        <Columns
          justifyContent="center"
          className="flex flex-1 items-center justify-center self-center pb-10 pt-4"
        >
          <img
            src="/tim_icon.png"
            alt="Logo da empresa TIM"
            className="h-24 animate-pulse"
          />
        </Columns>
      </div>
    );
  }

  return (
    <div className="min-w-screen flex min-h-screen items-center justify-center">
      <img
        src="/tim_icon.png"
        alt="Logo da empresa TIM"
        className="h-24 animate-pulse"
      />
    </div>
  );
};

export default ResumePage;
